import type { ChangeEvent, JSX } from "react";
import { TextField, Typography } from "@mui/material";
import { bound, observable, useObservable } from "ecce-react";
import { SignUpStep } from "../step";
import { useAutofocus } from "../util";


export class VerifyEmailAddressStep extends SignUpStep {
	override title: string = "Verify Email Address";
	override view: () => JSX.Element = () => <VerifyEmailAddressStepView step={ this }/>;

	#hash: string | null = null;

	#invalid: boolean = false;
	get invalid(): boolean { return this.#invalid; }
	@observable() private set invalid(value: boolean) { this.#invalid = value; }

	#code: string = "";
	get code(): string { return this.#code; }
	@observable() private set code(value: string) { this.#code = value; }

	setHash(hash: string): void {
		this.#hash = hash;
	}

	get hash(): string {
		if(!this.#hash) {
			throw new Error("Hash was null.");
		}

		return this.#hash;
	}

	protected override async handleSubmit(): Promise<void> {
		const response = await this.app.api.checkVerificationCode({
			hash: this.hash,
			code: this.code,
		});

		if(!response.ok) {
			console.error("Bad response:", response); // eslint-disable-line no-console
			alert(response.error.message); // eslint-disable-line no-alert
			return;
		}

		if(response.data.valid) {
			this.app.onEmailVerified();
			return;
		}

		this.invalid = !response.data.valid;
	}

	@bound()
	handleCodeChange(ev: ChangeEvent<HTMLInputElement>): void {
		this.code = ev.target.value;
		this.invalid = false;
	}

	get emailAddress(): string {
		return this.app.emailAddress;
	}
}

const VERIFY_DESCRIPTION_ID = "purser__codeDescription";
export const VerifyEmailAddressStepView = (p: { step: VerifyEmailAddressStep }): JSX.Element => {
	useObservable(p.step);
	const autofocus = useAutofocus();

	return (
		<>
			<div id={ VERIFY_DESCRIPTION_ID }>
				<Typography component="p">
					We have sent an email to <strong>{ p.step.emailAddress }</strong>{ " " }
					containing a verification code.
				</Typography>

				<Typography component="p" variant="body2">
					Enter that code below to verify you email address.
				</Typography>
			</div>

			<TextField
				name="code"
				label="Verification Code"
				required
				aria-describedby={ VERIFY_DESCRIPTION_ID }
				error={ p.step.invalid }
				helperText={ p.step.invalid ? "This code was not correct." : null }
				onChange={ p.step.handleCodeChange }
				inputRef={ autofocus }
				sx={{ marginY: "auto", textAlign: "center" }}
			/>
		</>
	);
};
